// Copyright ©️ 2026 eVolve MEP, LLC

import { useElementSize } from '@mantine/hooks';

import { InchesInput, type InchesInputProps } from 'components/Mantine/InchesInput';
import { isNil } from 'helpers/isNotNil';

import classes from './BendDimensionInput.module.css';
import { StepButton } from './StepButton';

const SIXTEENTH_INCH_IN_FEET = 1 / 16 / 12;

type Props = InchesInputProps & {
  /** Not configurable: `value` is always in decimal feet. */
  valueInFeet?: never;
  /** Amount each − / + tap changes the value by, in decimal feet. */
  step: number;
  /** Called with the new value, in decimal feet, after a − / + tap. */
  onStep: (value: number) => void;
};

/**
 * A touch-sized length field, in decimal feet but shown as inches, with − and + buttons that step the value by `step`.
 * Stepped values are rounded to a sixteenth of an inch and never go below zero.
 */
export const BendLengthStepper = ({ step, onStep, ...inchesInputProps }: Props) => {
  const { value, disabled, readOnly } = inchesInputProps;
  const locked = disabled || readOnly;

  const stepBy = (direction: 1 | -1) => {
    const stepped = (value ?? 0) + direction * step;
    const next = Math.max(0, Math.round(stepped / SIXTEENTH_INCH_IN_FEET) * SIXTEENTH_INCH_IN_FEET);
    if (next !== value) onStep(next);
  };

  const { ref: leftSectionRef, width: leftSectionWidth } = useElementSize<HTMLDivElement>();
  const { ref: rightSectionRef, width: rightSectionWidth } = useElementSize<HTMLDivElement>();

  return (
    <InchesInput
      size="xl"
      classNames={{ label: classes.label, input: classes.input, error: classes.error }}
      leftSectionWidth={leftSectionWidth}
      leftSectionPointerEvents="all"
      leftSection={
        <StepButton
          direction="decrease"
          onClick={() => stepBy(-1)}
          disabled={locked || isNil(value) || value <= 0}
          ref={leftSectionRef}
        />
      }
      rightSectionWidth={rightSectionWidth}
      rightSectionPointerEvents="all"
      rightSection={
        <StepButton direction="increase" onClick={() => stepBy(1)} disabled={locked} ref={rightSectionRef} />
      }
      {...inchesInputProps}
      valueInFeet
    />
  );
};
